import { useState, useEffect, useRef } from 'react';

interface HeroSlide {
  id: string;
  url: string;
  service: string;
  category: string;
  accentColor: string;
  highlightColor: string;
  tagline: string;
  description: string;
  badge: string;
  isIntro?: boolean;
}

const slides: HeroSlide[] = [
  /* ── 0 · Intro ─────────────────────────────────────────────── */
  {
    id: 'intro',
    url: 'https://readdy.ai/api/search-image?query=breathtaking%20futuristic%20AI%20technology%20command%20center%20ultra%20wide%20panoramic%20view%20enormous%20curved%20holographic%20displays%20glowing%20neural%20network%20data%20streams%20floating%203D%20charts%20deep%20midnight%20navy%20black%20atmosphere%20dramatic%20volumetric%20god%20rays%20light%20shafts%20cinematic%20architectural%20photography%20ultra%20sharp%208k%20luxury%20premium%20editorial%20award%20winning%20professional%20rich%20contrast%20moody%20lighting&width=1920&height=1080&seq=hero-intro-v12&orientation=landscape',
    service: 'nexaurA Infinity',
    category: 'Welcome to the Future',
    accentColor: '#93C5FD',
    highlightColor: '#60A5FA',
    tagline: 'AI · Automation · Intelligence',
    description: 'We build intelligent systems that transform how businesses operate — from smart apps and voice bots to industrial IoT and predictive AI.',
    badge: 'Next-Gen Technology',
    isIntro: true,
  },
  /* ── 1 · Web Design & Development ─────────────────────────── */
  {
    id: 'web-dev',
    url: 'https://readdy.ai/api/search-image?query=ultra%20premium%20web%20design%20and%20development%20showcase%20beautiful%20modern%20website%20displayed%20on%20massive%20curved%20ultra-wide%20monitor%20glowing%20electric%20blue%20UI%20wireframes%20and%20code%20floating%20holographically%20around%20deep%20midnight%20navy%20studio%20workspace%20two%20monitors%20showing%20stunning%20landing%20page%20design%20stunning%20gradient%20interface%20cobalt%20indigo%20accent%20neon%20lighting%20dramatic%20cinematic%208k%20ultra%20sharp%20award%20winning%20technology%20editorial%20professional%20photography&width=1920&height=1080&seq=hero-web-v12&orientation=landscape',
    service: 'Web Design & Development',
    category: 'Digital Experience',
    accentColor: '#67E8F9',
    highlightColor: '#22D3EE',
    tagline: 'Beautiful. Fast. Built to Convert.',
    description: 'We craft pixel-perfect, SEO-ready websites and enterprise portals engineered for speed, stunning UX, and measurable business results.',
    badge: 'UI/UX · Full-Stack',
  },
  /* ── 2 · Mobile App ────────────────────────────────────────── */
  {
    id: 'mobile-app',
    url: 'https://readdy.ai/api/search-image?query=ultra%20premium%20mobile%20app%20development%20cinematic%20showcase%20three%20latest%20flagship%20smartphones%20floating%20in%20dramatic%20dark%20studio%20with%20gorgeous%20glowing%20app%20UI%20screens%20electric%20sky%20blue%20and%20navy%20gradient%20dashboards%20holographic%20app%20interface%20cards%20orbiting%20around%20devices%20dramatic%20cobalt%20rim%20backlighting%208k%20ultra%20sharp%20award%20winning%20luxury%20technology%20product%20photography%20editorial%20professional%20dark%20background&width=1920&height=1080&seq=hero-mobile-v12&orientation=landscape',
    service: 'Mobile App Development',
    category: 'Cross-Platform Mobile',
    accentColor: '#BAE6FD',
    highlightColor: '#7DD3FC',
    tagline: 'iOS & Android. One Codebase. Zero Compromise.',
    description: 'Native-quality apps built with Flutter and React Native — intuitive interfaces, buttery smooth performance, and enterprise-grade scalability.',
    badge: 'Flutter · React Native',
  },
  /* ── 3 · WhatsApp Automation ───────────────────────────────── */
  {
    id: 'whatsapp-automation',
    url: 'https://readdy.ai/api/search-image?query=sophisticated%20premium%20business%20automation%20visualization%20glowing%20electric%20teal%20cyan%20interconnected%20workflow%20pipeline%20automation%20nodes%20holographic%20WhatsApp%20chat%20message%20bubbles%20CRM%20dashboard%20data%20streams%20auto%20reply%20bots%20floating%20icons%20deep%20dark%20navy%20blue%20background%20professional%20neon%20blue%20teal%20accent%20lighting%20luxury%20technology%20cinematic%208k%20render%20editorial%20award%20winning%20atmosphere&width=1920&height=1080&seq=hero-auto-v12&orientation=landscape',
    service: 'WhatsApp Automation',
    category: 'Smart Automation',
    accentColor: '#6EE7B7',
    highlightColor: '#34D399',
    tagline: 'Set It. Forget It. Watch It Scale.',
    description: 'Automate every customer touchpoint — WhatsApp flows, CRM sync, bulk campaigns, and intelligent auto-replies that work 24/7 without human effort.',
    badge: 'WhatsApp API · CRM',
  },
  /* ── 4 · Voice Bots & AI Assistants ───────────────────────── */
  {
    id: 'voice-bots',
    url: 'https://readdy.ai/api/search-image?query=cinematic%20futuristic%20AI%20voice%20assistant%20visualization%20spectacular%20glowing%20electric%20royal%20blue%20sound%20frequency%20waveforms%20holographic%20humanoid%20silhouette%20dissolving%20into%20luminous%20cobalt%20violet%20audio%20particles%20multilingual%20speech%20recognition%20neural%20synaptic%20connections%20deep%20navy%20midnight%20blue%20atmosphere%20ultra%20premium%203D%20render%20dramatic%20artistic%20lighting%208k%20award%20winning%20digital%20art%20sophisticated%20professional&width=1920&height=1080&seq=hero-voice-v12&orientation=landscape',
    service: 'Voice Bots & AI Assistants',
    category: 'Conversational AI',
    accentColor: '#C4B5FD',
    highlightColor: '#A78BFA',
    tagline: 'Human-Like AI That Listens, Thinks & Responds.',
    description: 'Deploy intelligent multilingual voice and chat assistants that handle customer support, HR queries, and operational workflows naturally and at scale.',
    badge: 'NLP · Multilingual',
  },
  /* ── 5 · AI & Machine Learning ─────────────────────────────── */
  {
    id: 'ai-ml',
    url: 'https://readdy.ai/api/search-image?query=magnificent%20artificial%20intelligence%20machine%20learning%20spectacular%203D%20neural%20network%20architecture%20billions%20of%20glowing%20electric%20blue%20golden%20amber%20synaptic%20connections%20forming%20complex%20predictive%20thought%20patterns%20data%20streams%20flowing%20through%20interconnected%20layers%20deep%20rich%20navy%20black%20background%20premium%20scientific%20visualization%20cinematic%20volumetric%20lighting%20ultra%208k%20award%20winning%20technology%20art%20dramatic%20professional%20moody&width=1920&height=1080&seq=hero-aiml-v12&orientation=landscape',
    service: 'AI & Machine Learning',
    category: 'Predictive Intelligence',
    accentColor: '#FDE68A',
    highlightColor: '#FCD34D',
    tagline: 'Predict. Automate. Dominate Your Market.',
    description: 'Custom ML models for demand forecasting, fraud detection, churn prediction, and real-time analytics that turn your data into unstoppable competitive advantage.',
    badge: 'Python · TensorFlow',
  },
  /* ── 6 · Computer Vision ────────────────────────────────────── */
  {
    id: 'computer-vision',
    url: 'https://readdy.ai/api/search-image?query=cutting%20edge%20computer%20vision%20AI%20system%20glowing%20digital%20eye%20iris%20rendered%20in%20spectacular%203D%20detail%20electric%20blue%20scanning%20laser%20beams%20augmented%20reality%20bounding%20box%20detection%20overlays%20floating%20holographic%20quality%20inspection%20panels%20smart%20factory%20dark%20navy%20background%20cobalt%20blue%20neon%20accent%20dramatic%20cinematic%20lens%20flare%20depth%20of%20field%20ultra%20premium%208k%20editorial%20technology%20photography%20award%20winning&width=1920&height=1080&seq=hero-vision-v12&orientation=landscape',
    service: 'Computer Vision',
    category: 'Vision AI',
    accentColor: '#FCA5A5',
    highlightColor: '#F87171',
    tagline: 'See Everything. Miss Nothing.',
    description: 'AI-powered visual inspection that catches defects, reads documents, and verifies quality at machine speed — outperforming manual checks every single time.',
    badge: 'YOLO · OpenCV · OCR',
  },
  /* ── 7 · IoT & Industrial Automation ───────────────────────── */
  {
    id: 'iot-automation',
    url: 'https://readdy.ai/api/search-image?query=spectacular%20Industry%204.0%20smart%20factory%20cinematic%20aerial%20wide%20shot%20massive%20illuminated%20industrial%20production%20floor%20glowing%20IoT%20sensor%20network%20nodes%20electric%20blue%20cobalt%20data%20streams%20connecting%20machines%20holographic%20SCADA%20monitoring%20dashboard%20overlay%20dramatic%20industrial%20architecture%20volumetric%20fog%20premium%20editorial%20photography%20ultra%20sharp%208k%20award%20winning%20deep%20steel%20navy%20shadows%20cinematic%20moody&width=1920&height=1080&seq=hero-iot-v12&orientation=landscape',
    service: 'IoT & Industrial Automation',
    category: 'Industry 4.0',
    accentColor: '#86EFAC',
    highlightColor: '#4ADE80',
    tagline: 'Connect Every Machine. Control Every Outcome.',
    description: 'End-to-end IoT architectures with PLC integration, real-time monitoring dashboards, and predictive maintenance that slash downtime and cut operational costs.',
    badge: 'PLC · SCADA · MQTT',
  },
];

/* ─── Rotating words for intro slide ───────────────────────────── */
const rotatingWords = ['Automation', 'AI Assistants', 'IoT Systems', 'Voice Bots', 'Smart Sites', 'ML Pipelines', 'Vision AI'];

function RotatingWord({ color }: { color: string }) {
  const [idx, setIdx] = useState(0);
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setShow(false);
      setTimeout(() => { setIdx((i) => (i + 1) % rotatingWords.length); setShow(true); }, 320);
    }, 2400);
    return () => clearInterval(t);
  }, []);
  return (
    <span
      className="font-black text-base sm:text-lg md:text-xl"
      style={{
        color,
        display: 'inline-block',
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.32s ease, transform 0.32s ease',
        fontFamily: "'Poppins', sans-serif",
        minWidth: '140px',
        smMinWidth: '180px',
        mdMinWidth: '220px',
        textAlign: 'center',
      }}
    >
      {rotatingWords[idx]}
    </span>
  );
}

/* ─── Letter-drop animated service name ────────────────────────── */
function AnimatedServiceName({ text, color }: { text: string; color: string }) {
  return (
    <span className="inline-flex flex-wrap justify-center">
      {text.split('').map((char, i) => (
        <span
          key={i}
          style={{
            color,
            display: 'inline-block',
            animation: 'letterDrop 0.55s cubic-bezier(0.22,1,0.36,1) both',
            animationDelay: `${i * 26}ms`,
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );
}



/* ─── Main component ────────────────────────────────────────────── */
export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(slides.length - 1);
  const [nameKey, setNameKey] = useState(0);
  const [textIn, setTextIn] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const advance = (to: number) => {
    setTextIn(false);
    setTimeout(() => {
      setPrev(current);
      setCurrent(to);
      setNameKey((k) => k + 1);
      setTimeout(() => setTextIn(true), 120);
    }, 850);
  };

  const startTimer = (from: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((c) => {
        const next = (c + 1) % slides.length;
        setTextIn(false);
        setTimeout(() => {
          setPrev(c);
          setCurrent(next);
          setNameKey((k) => k + 1);
          setTimeout(() => setTextIn(true), 120);
        }, 850);
        return c;
      });
    }, 6000);
  };

  useEffect(() => {
    setTimeout(() => setTextIn(true), 500);
    startTimer(0);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (idx: number) => {
    if (idx === current) return;
    if (timerRef.current) clearInterval(timerRef.current);
    advance(idx);
    startTimer(idx);
  };

  const slide = slides[current];
  const isIntro = !!slide.isIntro;

  return (
    <section id="home" className="relative w-full h-screen min-h-[680px] overflow-hidden">

      {/* ── Background images ─────────────────────────────────── */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0"
          style={{
            zIndex: i === current ? 2 : i === prev ? 1 : 0,
            opacity: i === current ? 1 : 0,
            transition: 'opacity 1.2s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <div
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: `url('${s.url}')`,
              animation: i === current ? 'heroKenBurns 7s ease-out forwards' : 'none',
            }}
          />
        </div>
      ))}

      {/* ── Overlays ──────────────────────────────────────────── */}
      <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(to bottom, rgba(10,31,68,0.72) 0%, rgba(10,31,68,0.42) 40%, rgba(10,31,68,0.85) 100%)' }} />
      <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(to right, rgba(10,31,68,0.55) 0%, transparent 50%, rgba(10,31,68,0.2) 100%)' }} />

      {/* ── Left slide-strip nav ───────────────────────────────── */}
      <div className="absolute left-0 top-0 bottom-0 z-30 w-1 hidden md:flex flex-col">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className="flex-1 cursor-pointer transition-all duration-500"
            style={{ background: i === current ? slide.accentColor : 'rgba(255,255,255,0.1)' }}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>



      {/* ── Main content ──────────────────────────────────────── */}
      <div className="relative z-20 w-full h-full flex flex-col items-center justify-center text-center px-4 sm:px-8 md:px-16">
        <div className="w-full max-w-5xl flex flex-col items-center">

          {/* Badge */}
          <div
            key={`badge-${nameKey}`}
            className="mb-3 sm:mb-4 px-2"
            style={{
              opacity: textIn ? 1 : 0,
              transform: textIn ? 'translateY(0) scale(1)' : 'translateY(-14px) scale(0.94)',
              transition: 'opacity 0.5s ease 0ms, transform 0.5s ease 0ms',
            }}
          >
            <span
              className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[9px] sm:text-[10px] font-bold tracking-[1.5px] sm:tracking-[2px] uppercase backdrop-blur-sm"
              style={{
                color: slide.accentColor,
                border: `1px solid ${slide.accentColor}35`,
                background: `${slide.accentColor}12`,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: slide.accentColor }}
              />
              <span className="whitespace-nowrap">{isIntro ? slide.category : slide.category}</span>
              {!isIntro && (
                <>
                  <span className="hidden sm:inline" style={{ color: `${slide.accentColor}55` }}>·</span>
                  <span className="hidden sm:inline" style={{ color: `${slide.accentColor}88` }}>{slide.badge}</span>
                </>
              )}
            </span>
          </div>

          {/* Headline */}
          <div
            key={`title-${nameKey}`}
            className="mb-5 w-full px-2"
            style={{
              opacity: textIn ? 1 : 0,
              transition: 'opacity 0.35s ease 0.1s',
            }}
          >
            {isIntro ? (
              <h1
                className="text-2xl sm:text-3xl md:text-[clamp(2rem,4.5vw,3.8rem)] font-black leading-[1.2] sm:leading-[1.15] tracking-tight text-white"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  animation: textIn ? 'wordRise 0.7s cubic-bezier(0.22,1,0.36,1) both 0.1s' : 'none',
                }}
              >
                <span className="block">AI-Powered Engineering &amp;</span>
                <span className="block">
                  <span style={{ color: '#F59E0B' }}>Intelligence</span>{' '}
                  <span className="hidden sm:inline">for Smarter Business</span>
                </span>
                <span className="block sm:hidden">for Smarter Business</span>
              </h1>
            ) : (
              <h1
                className="text-xl sm:text-2xl md:text-[clamp(1.75rem,4.5vw,3.5rem)] font-black leading-tight tracking-tight px-1"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                <AnimatedServiceName text={slide.service} color={slide.accentColor} />
              </h1>
            )}
          </div>

          {/* Tagline / rotating word */}
          <div
            key={`tag-${nameKey}`}
            className="mb-4 sm:mb-5 px-2"
            style={{
              opacity: textIn ? 1 : 0,
              transform: textIn ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 0.5s ease 0.22s, transform 0.5s ease 0.22s',
            }}
          >
            {isIntro ? (
              <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 justify-center">
                <span className="text-xs sm:text-sm md:text-base font-medium text-white/50" style={{ fontFamily: "'Inter', sans-serif" }}>
                  We build
                </span>
                <RotatingWord color={slide.highlightColor} />
                <span className="text-xs sm:text-sm md:text-base font-medium text-white/50" style={{ fontFamily: "'Inter', sans-serif" }}>
                  that scale
                </span>
              </div>
            ) : (
              <p
                className="text-xs sm:text-sm md:text-lg font-semibold italic px-2"
                style={{ color: `${slide.accentColor}CC`, fontFamily: "'Inter', sans-serif" }}
              >
                — {slide.tagline}
              </p>
            )}
          </div>

          {/* Description */}
          <div
            key={`desc-${nameKey}`}
            className="mb-6 sm:mb-8 md:mb-10 px-3 sm:px-4"
            style={{
              opacity: textIn ? 1 : 0,
              transform: textIn ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.55s ease 0.35s, transform 0.55s ease 0.35s',
            }}
          >
            <p
              className="text-xs sm:text-sm md:text-[15px] max-w-xs sm:max-w-lg md:max-w-xl mx-auto leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif", color: 'rgba(255,255,255,0.82)' }}
            >
              {slide.description}
            </p>
          </div>

          {/* Dot indicators */}
          <div
            style={{
              opacity: textIn ? 1 : 0,
              transition: 'opacity 0.5s ease 0.75s',
            }}
            className="flex items-center gap-2"
          >
            {slides.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goTo(i)}
                className="cursor-pointer transition-all duration-400 rounded-full flex-shrink-0"
                style={{
                  width: i === current ? '28px' : '7px',
                  height: '7px',
                  background: i === current ? slide.accentColor : 'rgba(255,255,255,0.25)',
                }}
                aria-label={s.service}
              />
            ))}
          </div>

        </div>
      </div>

      {/* ── Scroll indicator ──────────────────────────────────── */}
      <div
        className="absolute bottom-8 right-8 z-30 hidden md:flex flex-col items-center gap-2"
        style={{ opacity: textIn ? 1 : 0, transition: 'opacity 0.5s ease 1s' }}
      >
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/35 to-white/08" />
        <div
          className="w-7 h-7 flex items-center justify-center rounded-full border border-white/18"
          style={{ animation: 'scrollBounce 2s ease-in-out infinite' }}
        >
          <i className="ri-arrow-down-line text-white/45 text-xs w-3 h-3 flex items-center justify-center" />
        </div>
      </div>



      <style>{`
        @keyframes heroKenBurns {
          from { transform: scale(1.0) translateX(0); }
          to   { transform: scale(1.09) translateX(-14px); }
        }
        @keyframes letterDrop {
          0%   { opacity: 0; transform: translateY(-24px) rotateX(65deg); }
          65%  { transform: translateY(3px) rotateX(-4deg); }
          100% { opacity: 1; transform: translateY(0) rotateX(0); }
        }
        @keyframes wordRise {
          0%   { opacity: 0; transform: translateY(36px) scale(0.9); }
          65%  { transform: translateY(-5px) scale(1.01); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes scrollBounce {
          0%, 100% { opacity: 0.45; transform: translateY(0); }
          50%       { opacity: 0.9;  transform: translateY(4px); }
        }
        @keyframes statIn {
          from { opacity: 0; transform: translateY(14px) scale(0.88); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </section>
  );
}
